import WorkoutSuggestion from "./pages/fitness/WorkoutSuggestion";
import { useEffect, useRef, useState, lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { installIOSAudioUnlock } from "@/lib/iosAudioUnlock";
import { initOneSignal, registerUserForPush } from "@/lib/oneSignal";
import { ensureWhisperTimezone } from "@/lib/whisperPrefs";
import { syncSignupAttribution } from "@/lib/attribution";
import { metaAdvancedMatch } from "@/lib/metaPixel";
import { initRevenueCat, rcLogIn, rcLogOut } from "@/lib/revenuecat";
import { supabase } from "@/integrations/supabase/client";
import { exchangeNativeOAuthOnce } from "@/lib/oauthExchange";
import { invalidateAvatarCache } from "@/hooks/useAvatar";
import { useOnboardingGate } from "@/hooks/useOnboardingGate";
import { RouteErrorBoundary } from "@/components/RouteErrorBoundary";
import { Sentry } from "@/lib/sentry";
import { SentryRoutes } from "@/components/SentryRoutes";
import { analytics, identifyUser, resetAnalytics } from "@/lib/analytics";
import { AnalyticsBridge } from "@/components/AnalyticsBridge";
import { Analytics as VercelAnalytics } from "@vercel/analytics/react";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "@/components/ThemeProvider";
import { LanguageProvider } from "@/i18n/LanguageProvider";
import { PremiumProvider } from "@/contexts/PremiumContext";
import { PaywallProvider } from "@/contexts/PaywallContext";
import { BabyProvider } from "@/contexts/BabyContext";
import { BABY_MODULE_ENABLED, SHOW_ONBOARDING_INTRO } from "@/lib/featureFlags";
import { AppActivityProvider } from "@/contexts/AppActivityContext";
import { BrainPreferencesProvider } from "@/contexts/BrainPreferences";
import { ReportedContentProvider } from "@/contexts/ReportedContentContext";
import ActivityBanner from "@/components/ActivityBanner";
import MediaBanner from "@/components/MediaBanner";
import BabyActiveTimerBanner from "@/components/BabyActiveTimerBanner";
import Welcome from "./pages/Welcome";
import Landing from "./pages/Landing";
import Auth from "./pages/Auth";
import AuthCallback from "./pages/AuthCallback";
import ConfirmEmail from "./pages/ConfirmEmail";
import { SplashHero } from "@/features/splash/SplashHero";
import { useSplashSeen } from "@/features/splash/useSplashSeen";
import OnboardingIdentity from "./pages/OnboardingIdentity";
import OnboardingAvatar from "./pages/OnboardingAvatar";
import Processing from "./pages/Processing";

import MirrorHome from "./pages/MirrorHome";
import MirrorChat from "./pages/MirrorChat";
import LanguageLive from "./pages/LanguageLive";
import DailyCheckIn from "./pages/DailyCheckIn";
import Progress from "./pages/Progress";
import SettingsPage from "./pages/SettingsPage";
import SettingsProfile from "./pages/SettingsProfile";
import SettingsAvatars from "./pages/SettingsAvatars";
import SettingsVoice from "./pages/SettingsVoice";
import SettingsReminders from "./pages/SettingsReminders";
import VitalFlow from "./pages/VitalFlow";
import TeachMirror from "./pages/TeachMirror";
import Agenda from "./pages/Agenda";
import Tasks from "./pages/Tasks";
import Habits from "./pages/Habits";
import Goals from "./pages/Goals";
import Notes from "./pages/Notes";
import Finance from "./pages/Finance";
import Sleep from "./pages/Sleep";
import MyWork from "./pages/MyWork";
import Focus from "./pages/Focus";
import Gratitude from "./pages/Gratitude";
import Energy from "./pages/Energy";
import Contacts from "./pages/Contacts";
import Learning from "./pages/Learning";
import TimeTracker from "./pages/TimeTracker";
import Modules from "./pages/Modules";
import RouteFallback from "@/components/RouteFallback";
import Baby from "./pages/Baby";
import Fitness from "./pages/Fitness";
import FitnessWorkout from "./pages/FitnessWorkout";
import FitnessWorkoutRuntimeProvider from "./components/fitness/FitnessWorkoutRuntimeProvider";
import FitnessPlans from "./pages/fitness/FitnessPlans";
import FitnessGuidedPreview from "./pages/fitness/FitnessGuidedPreview";
import FitnessBodyMap from "./pages/fitness/FitnessBodyMap";
import FitnessProgress from "./pages/fitness/FitnessProgress";
import FitnessOnboarding from "./pages/fitness/FitnessOnboarding";
import FitnessLibrary from "./pages/fitness/FitnessLibrary";
import ExerciseDetail from "./pages/fitness/ExerciseDetail";
import FreeWorkoutGroupSelect from "@/pages/fitness/FreeWorkoutGroupSelect";
import FreeWorkoutExerciseSelect from "@/pages/fitness/FreeWorkoutExerciseSelect";
import FreeWorkoutReview from "./pages/fitness/FreeWorkoutReview";
import FitnessHistory from "./pages/fitness/FitnessHistory";
import FitnessBodyScan from "./pages/fitness/FitnessBodyScan";
import BabyFeeding from "./pages/BabyFeeding";
import BabySleep from "./pages/BabySleep";
import BabySounds from "./pages/BabySounds";
import BabyStimulation from "./pages/BabyStimulation";
import BabyStories from "./pages/BabyStories";
import BabyMilestones from "./pages/BabyMilestones";
import BabyGrowth from "./pages/BabyGrowth";
import VYVMoments from "./pages/VYVMoments";
import MirrorChallenge from "./pages/MirrorChallenge";
import IdentityCard from "./pages/IdentityCard";
import ScoreHistory from "./pages/ScoreHistory";
import MirrorWhisper from "./pages/MirrorWhisper";
import Nutrition from "./pages/Nutrition";
import Language from "./pages/Language";
import LanguageOnboarding from "./pages/LanguageOnboarding";
import LanguageLessonView from "./pages/LanguageLessonView";
import Pricing from "./pages/Pricing";
import Upgrade from "./pages/Upgrade";
import CreditsPage from "./pages/CreditsPage";
import CheckoutPremium from "./pages/CheckoutPremium";
import ResetPassword from "./pages/ResetPassword";
import { AppSplashProvider, useAppSplash } from "./contexts/AppSplashContext";
import AppSplash from "./components/splash/AppSplash";
import StaleVersionBanner from "./components/StaleVersionBanner";
import StravaCallback from "./pages/StravaCallback";
const PersonalActions = lazy(() => import("./pages/PersonalActions"));
const ExpansionConnection = lazy(() => import("./pages/ExpansionConnection"));
const ProactiveAssistant = lazy(() => import("./pages/ProactiveAssistant"));
const DocumentsPage = lazy(() => import("./pages/Documents"));
import SettingsConnections from "./pages/SettingsConnections";
import HealthMetrics from "./pages/HealthMetrics";
import Referral from "./pages/Referral";
import ReferralCapture from "./pages/ReferralCapture";
import NotFound from "./pages/NotFound";

// ── Lazy-loaded SECONDARY routes (perf, 14/06) ──────────────────────────────
// Code-split via React.lazy so these heavy, non-first-paint screens leave the
// main bundle. Each is reached by explicit navigation only and has ZERO other
// importers (verified) → clean per-route chunks. Suspense fallback below.
//
// DELIBERATELY NOT lazy-loaded (kept static): Home/Mirror, the avatar flow
// (frozen files), payments (Pricing/Upgrade/Credits/Checkout), login +
// onboarding, and the bottom-nav tabs (Check-in / Progress / Modules) — a
// Suspense flash on a tab switch would feel worse than a slightly larger
// initial bundle.
const Guide = lazy(() => import("./pages/Guide"));
// "LibraryPage" (não "Library") para não colidir com o ícone lucide de mesmo nome.
const LibraryPage = lazy(() => import("./pages/Library"));
const Meditation = lazy(() => import("./pages/Meditation"));
const MeditationSession = lazy(() => import("./pages/meditation/MeditationSession"));
const BrainTraining = lazy(() => import("./pages/BrainTraining"));
const MemorySequence = lazy(() => import("./pages/brain/MemorySequence"));
const SpeedReaction = lazy(() => import("./pages/brain/SpeedReaction"));
const WordLinks = lazy(() => import("./pages/brain/WordLinks"));
const DailyBrain = lazy(() => import("./pages/brain/DailyBrain"));
const Stroop = lazy(() => import("./pages/brain/Stroop"));
const AvatarProbe = lazy(() => import("./pages/dev/AvatarProbe"));
const OverflowProbe = lazy(() => import("./pages/dev/OverflowProbe"));
const LanguageCircuitPreview = lazy(() => import("./pages/LanguageCircuitPreview"));
const Relationships = lazy(() => import("./pages/Relationships"));
const VisionBoard = lazy(() => import("./pages/VisionBoard"));
const VYVSocial = lazy(() => import("./pages/VYVSocial"));
const Travel = lazy(() => import("./pages/Travel"));
const PrivacyPolicy = lazy(() => import("./pages/Legal/PrivacyPolicy"));
const CookiePolicy = lazy(() => import("./pages/Legal/CookiePolicy"));
const Terms = lazy(() => import("./pages/Legal/Terms"));
const DataDeletion = lazy(() => import("./pages/Legal/DataDeletion"));
const PrivacyRequest = lazy(() => import("./pages/PrivacyRequest"));
const Support = lazy(() => import("./pages/Support"));
const Agents = lazy(() => import("./pages/Agents"));

const queryClient = new QueryClient();

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

const SplashHost = () => {
  const splash = useAppSplash();
  return <AppSplash visible={splash.visible} />;
};

// Handles OAuth deep-link return on native (com.aivyv.mirror://auth/callback).
// Lives inside <BrowserRouter> so it can use useNavigate. Web is a no-op.
// Flow:
//   1. Provider redirects to the custom scheme after Google in-app browser.
//   2. Capacitor App emits appUrlOpen with the full URL (incl. ?code=...).
//   3. Close the SFSafariViewController and call exchangeCodeForSession.
//   4. Navigate to /auth/callback for the consent + profile-routing logic
//      that already exists for the web flow.
const NativeDeepLinkHandler = () => {
  const navigate = useNavigate();
  // Dedupe guards for the OAuth code exchange. iOS can emit appUrlOpen more than
  // once for the same callback; exchangeCodeForSession CONSUMES (deletes) the
  // one-time PKCE code_verifier on the first call, so a second exchange for the
  // same URL would fail with "code verifier should be non-empty" and bounce the
  // user back to /auth even though login actually succeeded. Skip re-entrant
  // fires (inFlight) and any repeat of an already-handled callback URL.
  const inFlightRef = useRef(false);
  const handledUrlRef = useRef<string | null>(null);
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    let cleanup: (() => void) | undefined;
    let active = true;
    (async () => {
      try {
        const [{ App: CapacitorApp }, { Browser }] = await Promise.all([
          import("@capacitor/app"),
          import("@capacitor/browser"),
        ]);
        if (!active) return;
        const handle = await CapacitorApp.addListener("appUrlOpen", async (event: { url: string }) => {
          const url = event?.url ?? "";
          if (!url.startsWith("com.aivyv.mirror://auth/callback")) return;
          // One exchange per callback — see inFlightRef/handledUrlRef above.
          if (inFlightRef.current || handledUrlRef.current === url) return;
          inFlightRef.current = true;
          try { await Browser.close(); } catch { /* sheet already gone */ }
          let result;
          try {
            // Shared single-exchange helper — dedupes by code across this
            // deep-link path and the /auth route effect (Auth.tsx), and strips
            // the ?code= from the URL on success so it can't be reprocessed.
            result = await exchangeNativeOAuthOnce(url);
          } finally {
            inFlightRef.current = false;
          }
          if (result.status === "error") {
            console.warn("[deep-link] exchange failed:", result.error);
            Sentry.addBreadcrumb({ category: "auth.oauth", level: "error", message: "exchange failed", data: { step: "exchange", url } });
            Sentry.captureException(result.error, { tags: { feature: "oauth_deeplink_exchange" }, contexts: { oauth: { step: "exchange" } } });
            navigate("/auth", { replace: true });
            return;
          }
          if (result.status === "no_code") {
            // Deep link without a code — not an auth callback we can complete.
            return;
          }
          // ok | skipped (already handled by the route effect or a prior fire).
          // Mark handled only now so a transient failure can be retried by a
          // subsequent (legitimate) re-fire.
          handledUrlRef.current = url;
          // Belt-and-suspenders: the pre-exchange close above can no-op if the
          // SFSafariViewController hadn't fully foregrounded the app yet. Close
          // again now that auth resolved so the sheet never lingers over the UI.
          try { await Browser.close(); } catch { /* sheet already gone */ }
          // Tell Auth the handshake landed so it tears down its OAuth recovery
          // (watchdog + dismiss listener) before unmounting — otherwise the
          // Browser.close above can flash a spurious "cancelled" toast.
          window.dispatchEvent(new Event("vyv:oauth-complete"));
          Sentry.addBreadcrumb({ category: "auth.oauth", level: "info", message: "exchange ok, navigating", data: { step: "navigate", url } });
          navigate("/auth/callback", { replace: true });
        });
        cleanup = () => { handle.remove(); };
      } catch (err) {
        console.error("[deep-link] listener setup failed:", err);
      }
    })();
    return () => { active = false; cleanup?.(); };
  }, [navigate]);
  return null;
};

// Entry routing (voice-first: "the Mirror is the app"). The presentation/landing
// routes ("/", "/welcome") decide where the app opens. The onboarding decision
// is centralized in useOnboardingGate (one place — also used by AppEntry):
//   1) active session + onboarding_complete   → /mirror  (the Espelho, push-to-
//        talk; NEVER /mirror/live — live é ação explícita do usuário e queima
//        mirror_live_seconds; o gate de plano é decidido em @/lib/liveAccess)
//   1b) active session + onboarding INCOMPLETE (no user_profiles row yet, OR
//        onboarding_complete != true)          → /onboarding/identity
//        (closes the half-onboarded leak across BOTH the native root and web /app)
//   2) no session but returning user flag      → /auth
//   3) otherwise                               → stay on /welcome (public landing)
// All other routes render as-is. "/" and "/welcome" remain public pages (Google
// brand verification) and are never redirected for case 3.
//
// WEB vs NATIVE split: on web the root "/" is now the public marketing Landing
// (see the "/" route), so it must NOT be gated/redirected here — only "/welcome"
// is an entry page on web. On native there is no Landing: "/" stays the app
// entry with the session+onboarding gate, so the iOS/Android shell keeps
// booting INTO the app (it loads dist/index.html at "/").
const ENTRY_PATHS = Capacitor.isNativePlatform()
  ? new Set(["/", "/welcome"])
  : new Set(["/welcome"]);

const EntryGate = ({ children }: { children: React.ReactNode }) => {
  const { pathname } = useLocation();
  // Voice-first flip: a completed user opens straight into the Espelho
  // (/mirror, push-to-talk) instead of /home. The half-onboarded gate is
  // preserved (→ /onboarding/identity). NEVER route entry to /mirror/live —
  // live é ação explícita do usuário e queima mirror_live_seconds (o gate de
  // plano é decidido em @/lib/liveAccess, não aqui).
  const target = useOnboardingGate({
    authedTarget: "/mirror",
    noSessionReturning: "/auth",
    noSessionFresh: null, // first visit → stay on the public landing
    enabled: ENTRY_PATHS.has(pathname),
  });

  // Hold the entry routes until the session check resolves so the landing
  // doesn't flash for returning/authenticated users.
  if (target === "pending") {
    return <div className="fixed inset-0" style={{ background: "#050810" }} />;
  }
  if (target) return <Navigate to={target} replace />;
  return <>{children}</>;
};

// Web app entry (/app). The web root "/" is the public Landing, so the logged-in
// web app lives here. SAME onboarding gate as EntryGate (closes the residual web
// leak where /app previously sent any session straight to /home WITHOUT checking
// onboarding): completed → /mirror, half-onboarded → /onboarding/identity, no
// session → /auth. Native never routes here (its root IS the app flow).
const AppEntry = () => {
  const target = useOnboardingGate({
    authedTarget: "/mirror",
    noSessionReturning: "/auth",
    noSessionFresh: "/auth",
  });
  if (target === "pending") {
    return <div className="fixed inset-0" style={{ background: "#050810" }} />;
  }
  return <Navigate to={target ?? "/auth"} replace />;
};

const MaybeBabyProvider = ({ children }: { children: React.ReactNode }) =>
  BABY_MODULE_ENABLED ? <BabyProvider>{children}</BabyProvider> : <>{children}</>;

// Gates the /auth route with the pre-login splash hero on the user's first
// session (or after the 30-day re-show window). The "Watch intro" button on
// the auth screen flips `forced` to replay the video on demand.
//
// SHOW_ONBOARDING_INTRO (default OFF) removes this whole intro step: the landing
// already plays the narrative video before login, so repeating it here was a
// duplicated step that hurt signup. With the flag OFF, /auth opens straight on
// the login form, the "Watch intro" replay button is hidden, and there is no
// SplashHero render at all — so no orphan video/black screen on the way to the
// post-login gate. Flip VITE_SHOW_ONBOARDING_INTRO=true to restore the old flow.
const AuthGate = () => {
  const { shouldShow, markSeen, forceShow } = useSplashSeen();
  const [forced, setForced] = useState(false);

  // Measure the gate's effect on conversion: fire once per fresh / re-show
  // session (the only case where the intro is even a candidate), tagging whether
  // the flag let the cinematic actually play. Returning users past the re-show
  // window aren't relevant, so they don't emit noise.
  const introDecisionFired = useRef(false);
  useEffect(() => {
    if (introDecisionFired.current || !shouldShow) return;
    introDecisionFired.current = true;
    analytics.onboardingIntro(SHOW_ONBOARDING_INTRO);
  }, [shouldShow]);

  if (SHOW_ONBOARDING_INTRO && (shouldShow || forced)) {
    return (
      <SplashHero
        onComplete={() => {
          markSeen();
          setForced(false);
        }}
      />
    );
  }
  return (
    <Auth
      // Hidden when the flag is OFF — no path back to the founder video.
      onShowIntro={
        SHOW_ONBOARDING_INTRO
          ? () => {
              forceShow();
              setForced(true);
            }
          : undefined
      }
    />
  );
};

// Fire app_opened exactly once per cold start, and only for an identified
// session (so it always carries $is_identified + distinct_id == user.id).
// Module scope — not React state — so a remount can't reset it; deduped against
// repeated INITIAL_SESSION / SIGNED_IN / TOKEN_REFRESHED deliveries.
let appOpenedFired = false;

// Tracks the user id the avatar cache was last busted for, so SIGNED_IN re-fires
// for the SAME user (token refresh / tab focus / app resume) don't invalidate the
// cache and race useAvatar's in-flight first fetch (the "spinning orb on entry"
// bug). Only a real user transition busts it.
let lastAvatarUserId: string | null = null;

const App = () => {
  useEffect(() => {
    initOneSignal().catch(() => {});
    // Configure RevenueCat (StoreKit) once at boot. No-op off iOS native /
    // when NATIVE_IAP_ENABLED is off; internally try/catch-guarded.
    initRevenueCat().catch(() => {});
    // iOS: resume the shared Web Audio context on the first user gesture so
    // Mirror TTS (routed through it for lip-sync) isn't silenced. No-op elsewhere.
    installIOSAudioUnlock();
  }, []);

  // Register user for push after auth
  useEffect(() => {
    const sub = supabase.auth.onAuthStateChange((event, session) => {
      // Bust the module-level avatar cache only on a REAL user transition —
      // sign-out, or sign-in as a DIFFERENT user — so a new login re-hydrates the
      // active avatar and a logout can't leak the prior user's. Supabase re-fires
      // SIGNED_IN for the SAME user (token refresh / tab focus / app resume);
      // invalidating on those raced useAvatar's in-flight first fetch and left
      // the Espelho on the no-avatar orb until a remount.
      const nextUserId = session?.user?.id ?? null;
      if (event === "SIGNED_OUT") {
        invalidateAvatarCache();
        lastAvatarUserId = null;
      } else if (nextUserId && nextUserId !== lastAvatarUserId) {
        invalidateAvatarCache();
        lastAvatarUserId = nextUserId;
      }
      if (session?.user?.id) {
        // Returning-user flag: on a future launch with no live session, EntryGate
        // sends them straight to /auth instead of the public landing.
        localStorage.setItem("vyv_has_authed", "true");
        Sentry.setUser({ id: session.user.id });
        // Awaits OneSignal init internally before login (the init→login race
        // left external_id empty on every subscription — prod 10/06).
        void registerUserForPush(session.user.id);
        // Bootstrap user_whisper_preferences.timezone on authenticated load
        // so reminder dispatch converts in the user's zone from day one.
        void ensureWhisperTimezone(session.user.id);
        // Atribuição de aquisição pelo caminho do OAuth, que não consegue
        // injetar metadata na transação de cadastro. Aqui é a AUTO-CURA: roda
        // em todo carregamento autenticado, é idempotente dos dois lados
        // (first-write-wins no servidor, marca por usuário no cliente), então
        // quem falhou no retorno do OAuth — o ponto onde o navegador in-app
        // derruba 55% dos eventos — é atribuído no boot seguinte.
        void syncSignupAttribution(session.user.id);
        // Correspondência avançada MANUAL do pixel: re-init com em/external_id/
        // fn CRUS (o pixel hasheia) assim que a sessão existe — login E
        // restauração. É o que dá match aos cadastros OAuth, onde a
        // correspondência automática (só campos de formulário) não vê nada.
        // Idempotente por usuário por page load; nunca bloqueia o boot.
        void metaAdvancedMatch(session.user.id, session.user.email);
        // RevenueCat: identify with the Supabase user.id so the Apple
        // entitlement maps back to this VYV user in the webhook. No-op
        // off iOS native; internally try/catch-guarded.
        rcLogIn(session.user.id);
        // Identify by Supabase user_id only (no PII). signup_provider comes
        // from the auth metadata; plan/language/has_avatar are set reactively
        // by AnalyticsBridge as they load.
        const provider = (session.user.app_metadata as { provider?: string } | undefined)?.provider;
        identifyUser(session.user.id, provider ? { signup_provider: provider } : undefined);
        // app_opened AFTER identify so it's attributed to user.id, never the
        // anonymous landing distinct_id. Once per cold start (module guard); does
        // not re-fire on TOKEN_REFRESHED or repeat session events.
        if (!appOpenedFired) {
          appOpenedFired = true;
          analytics.appOpened();
        }
      } else {
        Sentry.setUser(null);
        resetAnalytics();
        // RevenueCat: reset to an anonymous app_user_id on sign-out. No-op
        // off iOS native; internally try/catch-guarded.
        rcLogOut();
      }
    });
    return () => { sub.data.subscription.unsubscribe(); };
  }, []);

  return (
  <AppActivityProvider>
  <ThemeProvider>
    <LanguageProvider>
      <BrainPreferencesProvider>
      <PremiumProvider>
      <MaybeBabyProvider>
      <ReportedContentProvider>
      <QueryClientProvider client={queryClient}>
        <AppSplashProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <FitnessWorkoutRuntimeProvider>
            <PaywallProvider>
            <AnalyticsBridge />
            {/* Vercel Web Analytics — WEB ONLY (mesmo split de plataforma da
                rota "/" abaixo): no shell iOS/Android o WebView roda em
                origem capacitor/localhost, onde /_vercel/insights nem existe
                e as sessões contaminariam o funil da landing/webapp. Só
                injeta o script; pageviews são rastreados pelo próprio pacote
                via History API. Cookieless (sem categoria no Termly). */}
            {!Capacitor.isNativePlatform() && <VercelAnalytics />}
            <NativeDeepLinkHandler />
            <ActivityBanner />
            <MediaBanner />
            {BABY_MODULE_ENABLED && <BabyActiveTimerBanner />}
            <ScrollToTop />
            <EntryGate>
            <RouteErrorBoundary>
            {/* One Suspense boundary for the lazy secondary routes. Static
                (critical) routes are already loaded and never suspend, so this
                only shows RouteFallback briefly on first navigation to a
                code-split route — never a white screen. */}
            <Suspense fallback={<RouteFallback />}>
            <SentryRoutes>
              {/* Backward-compat: language is now chosen inside /welcome. */}
              <Route path="/onboarding/language" element={<Navigate to="/welcome" replace />} />
              {/* Root: WEB → public marketing Landing; NATIVE → app flow
                  (Welcome, which EntryGate redirects to /home or /auth by
                  session). This platform split is what keeps the iOS/Android
                  shell booting INTO the app, never the Landing. */}
              <Route path="/" element={Capacitor.isNativePlatform() ? <Welcome /> : <Landing />} />
              <Route path="/welcome" element={<Welcome />} />
              {/* Web app entry behind login (the Landing's "open web app" CTA). */}
              <Route path="/app" element={<AppEntry />} />
              <Route path="/auth" element={<AuthGate />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              {/* Cadastro por e-mail com "Confirm email" ligado: o signUp não
                  devolve sessão, então o corredor para aqui até o link ser
                  aberto. A própria tela sai sozinha se houver sessão. */}
              <Route path="/auth/confirm-email" element={<ConfirmEmail />} />
              <Route path="/onboarding/identity" element={<OnboardingIdentity />} />
              <Route path="/onboarding/avatar" element={<OnboardingAvatar />} />
              <Route path="/processing" element={<Processing />} />
              
              <Route path="/home" element={<MirrorHome />} />
              <Route path="/mirror" element={<MirrorChat />} />
              {/* Live (Option C) — full-screen avatar presence + push-to-talk,
                  same MirrorChat pipeline with chat chrome hidden. */}
              <Route path="/mirror/live" element={<MirrorChat live />} />
              {/* Language "Modo Professor" — same Live pipeline, tutor brain via
                  module_context="language_tutor". The LanguageLive wrapper adds
                  the vocabulary engine (day-set selection, mastery write-back,
                  XP, auto-kickoff) around MirrorChat. Premium-gated at the entry. */}
              <Route path="/language/live" element={<LanguageLive />} />
              <Route path="/check-in" element={<DailyCheckIn />} />
              <Route path="/progress" element={<Progress />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/settings/profile" element={<SettingsProfile />} />
              <Route path="/settings/avatars" element={<SettingsAvatars />} />
              <Route path="/settings/voice" element={<SettingsVoice />} />
              <Route path="/settings/reminders" element={<SettingsReminders />} />
              <Route path="/voice-clone" element={<Navigate to="/settings/voice" replace />} />
              <Route path="/vital-flow" element={<VitalFlow />} />
              <Route path="/teach-mirror" element={<TeachMirror />} />
              <Route path="/agenda" element={<Agenda />} />
              <Route path="/tasks" element={<Tasks />} />
              <Route path="/habits" element={<Habits />} />
              <Route path="/goals" element={<Goals />} />
              <Route path="/notes" element={<Notes />} />
              <Route path="/finance" element={<Finance />} />
              <Route path="/sleep" element={<Sleep />} />
              {/* mywork: allow-list por e-mail — a própria página redireciona
                  para /modules quem não está na lista (useMyWorkAccess). */}
              <Route path="/my-work" element={<MyWork />} />
              <Route path="/strava/callback" element={<StravaCallback />} />
              {/* Notion e Spotify NÃO têm página de callback: como no Google
                  Calendar / Google Tasks / Outlook, o redirect do provedor vai
                  direto para a edge function, que devolve o usuário em
                  /settings/connections?<provider>=connected. Só o Strava tem
                  página própria, por ser do padrão antigo com state em
                  sessionStorage. */}
              <Route path="/settings/connections" element={<SettingsConnections />} />
              <Route path="/actions" element={<Suspense fallback={null}><PersonalActions /></Suspense>} />
              <Route path="/connections/:provider" element={<Suspense fallback={null}><ExpansionConnection /></Suspense>} />
              <Route path="/briefing" element={<Suspense fallback={null}><ProactiveAssistant /></Suspense>} />
              <Route path="/documents" element={<Suspense fallback={null}><DocumentsPage /></Suspense>} />
              <Route path="/health/metrics" element={<HealthMetrics />} />
              <Route path="/r/:code" element={<ReferralCapture />} />
              <Route path="/referral" element={<Referral />} />
              <Route path="/focus" element={<Focus />} />
              <Route path="/gratitude" element={<Gratitude />} />
              <Route path="/energy" element={<Energy />} />
              <Route path="/contacts" element={<Contacts />} />
              <Route path="/learning" element={<Learning />} />
              <Route path="/time-tracker" element={<TimeTracker />} />
              <Route path="/modules" element={<Modules />} />
              <Route path="/library" element={<LibraryPage />} />
              <Route path="/guide" element={<Guide />} />
              <Route path="/fitness" element={<Fitness />} />
              <Route path="/fitness/onboarding" element={<FitnessOnboarding />} />
              <Route path="/fitness/workout" element={<FitnessWorkout />} />
              <Route path="/fitness/guided" element={<FitnessPlans />} />
              <Route path="/fitness/guided/preview" element={<FitnessGuidedPreview />} />
              <Route path="/fitness/plans" element={<Navigate to="/fitness/guided" replace />} />
              <Route path="/fitness/body-map" element={<FitnessBodyMap />} />
              <Route path="/fitness/progress" element={<FitnessProgress />} />
              <Route path="/fitness/history" element={<FitnessHistory />} />
              <Route path="/fitness/body-scan" element={<FitnessBodyScan />} />
              <Route path="/fitness/library" element={<FitnessLibrary />} />
              <Route path="/fitness/library/:exerciseId" element={<ExerciseDetail />} />
              <Route path="/fitness/free-workout" element={<FreeWorkoutGroupSelect />} />
              <Route path="/fitness/free-workout/exercises" element={<FreeWorkoutExerciseSelect />} />
              <Route path="/fitness/free-workout/review" element={<FreeWorkoutReview />} />
              <Route path="/fitness/suggestion" element={<WorkoutSuggestion />} />
              {BABY_MODULE_ENABLED && (
                <>
                  <Route path="/baby" element={<Baby />} />
                  <Route path="/baby/feeding" element={<BabyFeeding />} />
                  <Route path="/baby/sleep" element={<BabySleep />} />
                  <Route path="/baby/sounds" element={<BabySounds />} />
                  <Route path="/baby/stimulation" element={<BabyStimulation />} />
                  <Route path="/baby/stories" element={<BabyStories />} />
                  <Route path="/baby/milestones" element={<BabyMilestones />} />
                  <Route path="/baby/growth" element={<BabyGrowth />} />
                </>
              )}
              {/* Fallback: when module disabled (or stale cached URL), bounce to home */}
              <Route path="/baby/*" element={<Navigate to="/" replace />} />
              <Route path="/vyv-moments" element={<VYVMoments />} />
              <Route path="/score-history" element={<ScoreHistory />} />
              <Route path="/mirror-challenge" element={<MirrorChallenge />} />
              <Route path="/identity-card" element={<IdentityCard />} />
              <Route path="/mirror-whisper" element={<MirrorWhisper />} />
              <Route path="/nutrition" element={<Nutrition />} />
              <Route path="/meditation" element={<Meditation />} />
              <Route path="/meditation/session/:sessionId" element={<MeditationSession />} />
              <Route path="/brain-training" element={<BrainTraining />} />
              <Route path="/brain/memory-sequence" element={<MemorySequence />} />
              <Route path="/brain/speed-reaction" element={<SpeedReaction />} />
              <Route path="/brain/word-links" element={<WordLinks />} />
              <Route path="/brain/daily-brain" element={<DailyBrain />} />
              <Route path="/brain/stroop" element={<Stroop />} />
              <Route path="/relationships" element={<Relationships />} />
              <Route path="/vision-board" element={<VisionBoard />} />
              <Route path="/vyv-social" element={<VYVSocial />} />
              <Route path="/travel" element={<Travel />} />
              <Route path="/language" element={<LanguageCircuitPreview />} />
              <Route path="/language/library" element={<Language />} />
              <Route path="/agents" element={<Agents />} />
              <Route path="/language/setup" element={<LanguageOnboarding />} />
              <Route path="/language/circuit-preview" element={<LanguageCircuitPreview />} />
              <Route path="/language/:langCode" element={<LanguageLessonView />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/upgrade" element={<Upgrade />} />
              <Route path="/creditos" element={<CreditsPage />} />
              <Route path="/checkout/premium" element={<CheckoutPremium />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/cookies" element={<CookiePolicy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/data-deletion" element={<DataDeletion />} />
              <Route path="/privacy-request" element={<PrivacyRequest />} />
              <Route path="/support" element={<Support />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              {/* Visual-regression probes (headless QA): see the page files. */}
              <Route path="/dev/avatar-probe" element={<AvatarProbe />} />
              <Route path="/dev/overflow-probe" element={<OverflowProbe />} />
              <Route path="*" element={<NotFound />} />
            </SentryRoutes>
            </Suspense>
            </RouteErrorBoundary>
            </EntryGate>
            </PaywallProvider>
            </FitnessWorkoutRuntimeProvider>
          </BrowserRouter>
          <SplashHost />
          <StaleVersionBanner />
        </TooltipProvider>
        </AppSplashProvider>
      </QueryClientProvider>
      </ReportedContentProvider>
      </MaybeBabyProvider>
      </PremiumProvider>
      </BrainPreferencesProvider>
    </LanguageProvider>
  </ThemeProvider>
  </AppActivityProvider>
  );
};

export default App;
